import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { cx } from '@ui/classes'
import { CategoryImage } from './CategoryImage'

export interface CategoryGridItem {
  name: string
  /** Верхняя категория (отдел): из неё берётся цвет. */
  parentName?: string | null
  count: number
  hasImage?: boolean
}

export interface CategoryGridProps {
  items: CategoryGridItem[]
  onSelect: (name: string) => void
  /** Мобильный режим: две колонки, без подгонки под высоту. */
  mobile?: boolean
  /** Крупные цели касания (POS-терминал). */
  touch?: boolean
  testId?: string
}

interface GridMetrics {
  cols: number
  /** Высота плитки; ширина = высота × TILE_ASPECT. */
  tile: number
  compact: boolean
  height: number | null
}

const MIN_TILE = 88
const MAX_TILE_TOUCH = 300
const MAX_TILE_MOUSE = 260
const MAX_COLS = 8
/** Ширина плитки к высоте: слегка вытянутая по вертикали. */
const TILE_ASPECT = 0.86

/**
 * Сетка категорий, которая подбирает число колонок и размер плитки так, чтобы
 * все категории помещались в видимую область без прокрутки. Если места
 * физически не хватает (очень много категорий на маленьком экране), включается
 * компактный режим: подпись в одну строку, счётчик скрыт, появляется прокрутка.
 *
 * Плитки прямоугольные (уже и выше), светлые в обеих темах: SVG-иконки имеют
 * собственный светлый фон, поэтому плитка сливается с ним, а текст остаётся
 * чёрным и читаемым.
 */
export function CategoryGrid({
  items,
  onSelect,
  mobile = false,
  touch = false,
  testId = 'category-grid'
}: CategoryGridProps) {
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const [metrics, setMetrics] = useState<GridMetrics>({
    cols: mobile ? 2 : 4,
    tile: 140,
    compact: false,
    height: null
  })

  const measure = useCallback(() => {
    const el = wrapRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const width = rect.width
    if (width <= 0) return

    const gap = touch ? 10 : 8
    const count = items.length

    if (mobile) {
      const tileW = Math.max(MIN_TILE, Math.floor((width - gap) / 2))
      setMetrics({
        cols: 2,
        tile: Math.round(tileW / TILE_ASPECT),
        compact: false,
        height: null
      })
      return
    }

    // Доступная высота: от верха сетки до низа окна, но не ниже контейнера
    // каталога (у него min-h-0 и фиксированная высота). Промежуточные div
    // растягиваются по контенту, поэтому ищем ближайший min-h-0.
    const fitParent = el.closest('.min-h-0') as HTMLElement | null
    const parentRect = fitParent?.getBoundingClientRect()
    const byWindow = window.innerHeight - rect.top - 24
    // Запас 32px — подпись «Категории · N» под сеткой.
    const byParent = parentRect ? parentRect.bottom - rect.top - 32 : byWindow
    const available = Math.max(280, Math.min(byWindow, byParent))
    const maxTile = touch ? MAX_TILE_TOUCH : MAX_TILE_MOUSE

    let bestCols = 2
    let bestTile = 0
    let bestScore = 0
    for (let cols = 2; cols <= MAX_COLS; cols += 1) {
      const rows = Math.ceil(count / cols)
      const byWidth = (width - gap * (cols - 1)) / cols
      const byHeight = (available - gap * (rows - 1)) / rows
      // tile — высота; ширина колонки ограничивает её через TILE_ASPECT.
      const tile = Math.floor(Math.min(byWidth / TILE_ASPECT, byHeight, maxTile))
      // Оценка: крупная плитка + бонус за заполнение высоты окна.
      const fill = Math.min(1, (rows * tile) / available)
      const score = tile * (0.7 + 0.3 * fill)
      if (score > bestScore) {
        bestScore = score
        bestTile = tile
        bestCols = cols
      }
    }

    if (bestTile >= MIN_TILE) {
      setMetrics({
        cols: bestCols,
        tile: Math.floor(bestTile),
        compact: false,
        height: available
      })
      return
    }

    // Не влезает: компактные плитки и прокрутка внутри доступной высоты.
    const cols = Math.max(
      2,
      Math.min(MAX_COLS, Math.floor((width + gap) / (MIN_TILE + gap)))
    )
    setMetrics({ cols, tile: MIN_TILE, compact: true, height: available })
  }, [items.length, mobile, touch])

  useLayoutEffect(() => {
    measure()
  }, [measure])

  useEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const observer = new ResizeObserver(() => measure())
    observer.observe(el)
    window.addEventListener('resize', measure)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [measure])

  const gap = touch ? 10 : 8
  const tileW = Math.round(metrics.tile * TILE_ASPECT)
  // Иконка занимает большую часть плитки: SVG масштабируется без потерь.
  const imageSize = Math.max(
    32,
    Math.min(220, Math.round(Math.min(tileW, metrics.tile) * 0.78))
  )

  return (
    <div
      ref={wrapRef}
      className={cx(metrics.compact && 'overflow-y-auto')}
      style={metrics.height ? { height: metrics.height } : undefined}
      data-testid={testId}
    >
      <div
        className="grid justify-center"
        style={{
          gridTemplateColumns: `repeat(${metrics.cols}, ${tileW}px)`,
          gap,
          alignContent: 'start'
        }}
      >
        {items.map((c) => (
          <button
            key={c.name}
            type="button"
            data-testid="category-tile"
            className="relative flex flex-col items-center overflow-hidden rounded-card border border-lineStrong bg-[#F8FAFC] p-2 text-center text-slate-900 hover:bg-[#EEF2F7]"
            style={{ width: tileW, height: metrics.tile }}
            onClick={() => onSelect(c.name)}
            title={c.name}
          >
            <span className="flex min-h-0 w-full flex-1 items-center justify-center overflow-hidden">
              <CategoryImage
                name={c.name}
                parent={c.parentName}
                hasImage={c.hasImage}
                size={imageSize}
                rounded={16}
                className="max-h-full max-w-full object-contain"
              />
            </span>
            <span
              className={cx(
                'mt-1 w-full font-semibold leading-tight text-slate-900',
                metrics.compact ? 'line-clamp-1 text-tiny' : 'line-clamp-2 text-small'
              )}
            >
              {c.name}
            </span>
            {!metrics.compact && (
              <span className="text-[10px] leading-4 text-slate-500">{c.count}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  )
}
